import React, { useState, useEffect } from 'react';
import {
  UserCheck, ShieldCheck, Tag as TagIcon, CalendarX, Wrench,
  Clock, Sun, CalendarRange, Repeat, Users, CalendarClock, User, Eye, CheckCircle2,
} from 'lucide-react';
import { dashboardApi } from '../lib/api';

const fmt = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

const scopeLabel = (s) => (s === 'company' ? 'Company-wide' : s === 'department' ? 'Your department' : s === 'user' ? 'You' : s);
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

const TONES = {
  primary: 'bg-primary/10 text-primary',
  amber: 'bg-amber-500/10 text-amber-600',
  emerald: 'bg-emerald-500/10 text-emerald-600',
  violet: 'bg-violet-500/10 text-violet-600',
  sky: 'bg-sky-500/10 text-sky-600',
  rose: 'bg-rose-500/10 text-rose-600',
};

// One fact: a small round icon, its name, then what it says (Warm Hive, Rob's B1, 28/10/2026).
const Tile = ({ icon, tone = 'primary', title, children }) => (
  <div className="flex items-start gap-2.5 py-2 border-t border-dashed border-border first:border-t-0">
    <div className={`w-6 h-6 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5 ${TONES[tone]}`}>{icon}</div>
    <div className="min-w-0">
      <div className="text-xs font-bold text-muted-foreground">{title}</div>
      <div className="text-sm text-foreground/85 leading-snug">{children}</div>
    </div>
  </div>
);

// The facts are grouped by the question they answer (Rob, 28/10/2026: "B1"):
// when can I book, how does my year work, who signs it off.
const Group = ({ title, children }) => (
  <div className="rounded-[14px] border border-border/70 bg-field px-4 pt-3 pb-1.5 min-w-0">
    <h3 className="font-heading text-sm font-semibold mb-0.5">{title}</h3>
    <div>{children}</div>
  </div>
);

const YourSetupCard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    dashboardApi.getMySetup()
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const approvers = data?.approvers || [];
  const policy = data?.policy || null;
  const tags = data?.tags || [];
  const blackouts = data?.blackouts || [];
  const rules = data?.booking_rules || null;
  const bankHols = data?.bank_holidays || null;
  const leaveYear = data?.leave_year || null;
  const carry = data?.carry_over || null;
  const limits = data?.limits || [];
  const pattern = data?.working_pattern || null;
  const manager = data?.manager || null;
  const reach = data?.reach || null;
  const selfApproves = data?.self_approves || false;

  const bookingText = () => {
    const parts = [];
    if (rules?.min_notice_days > 0) parts.push(`${plural(rules.min_notice_days, 'day')}’ notice`);
    if (rules?.max_consecutive_days > 0) parts.push(`max ${plural(rules.max_consecutive_days, 'day')} in a row`);
    return parts.length ? parts.join(' · ') : 'No notice or length limits';
  };

  const carryText = () => {
    if (!carry?.enabled) return 'Unused days don’t carry over.';
    // ⚠️ A cap of 0 means NO LIMIT (09/10/2026 — it said "Up to 0 days into next year").
    let t = carry.max_days > 0
      ? `Up to ${plural(carry.max_days, 'day')} into next year`
      : 'Unused days carry into next year, with no limit';
    if (carry.expiry_months > 0) {
      t += carry.use_by ? `, to be used by ${carry.use_by}` : `, to be used within ${plural(carry.expiry_months, 'month')}`;
    }
    return t + '.';
  };

  return (
    <div className="card-hive p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Wrench className="w-5 h-5 text-hive-orange" /> Your setup
        </h2>
        <span className="text-xs text-muted-foreground">How your leave works</span>
      </div>
      {/* Always open (Rob, 28/10/2026: "should always be visible") — the open/close
          arrow went, and so did the allowance tile, which repeated the holiday card. */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading&hellip;</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Group title="When you can book">
            <Tile icon={<Clock className="w-4 h-4" />} tone="amber" title="Booking rules">
              {bookingText()}
            </Tile>

            <Tile icon={<CalendarX className="w-4 h-4" />} tone="rose" title="No-leave dates">
              {blackouts.length === 0 ? (
                <span>None affecting you.</span>
              ) : (
                <ul className="space-y-1">
                  {blackouts.map((b, i) => (
                    <li key={i}>
                      <span className="text-foreground font-medium">{b.name}</span>{' '}
                      <span>{fmt(b.start_date)}{b.end_date && b.end_date !== b.start_date ? ` – ${fmt(b.end_date)}` : ''}</span>{' '}
                      <span className="text-xs text-muted-foreground">({scopeLabel(b.scope)})</span>
                    </li>
                  ))}
                </ul>
              )}
            </Tile>

            {limits.length > 0 && (
              <Tile icon={<Users className="w-4 h-4" />} tone="rose" title="Team availability">
                <ul className="space-y-0.5">
                  {limits.map((l, i) => (
                    <li key={i}>Max <span className="text-foreground font-medium">{l.max_off}</span> off at once &middot; {l.label}</li>
                  ))}
                </ul>
              </Tile>
            )}

            {pattern && (
              <Tile icon={<CalendarClock className="w-4 h-4" />} tone="sky" title="Your working pattern">
                <span className="text-foreground">{pattern.label}</span>
                {pattern.works_bank_holidays && <span> &middot; works bank holidays</span>}
              </Tile>
            )}
          </Group>

          <Group title="Your leave year">
            {leaveYear && (
              <Tile icon={<CalendarRange className="w-4 h-4" />} tone="violet" title="Leave year">
                Runs from 1 {leaveYear.start_month}.
              </Tile>
            )}

            <Tile icon={<Repeat className="w-4 h-4" />} tone="emerald" title="Carry-over">
              {carryText()}
            </Tile>

            {bankHols && (
              <Tile icon={<Sun className="w-4 h-4" />} tone="sky" title="Bank holidays">
                {bankHols.included ? 'Counted within your allowance.' : 'Given on top of your allowance.'}
                {bankHols.region && <span> &middot; {bankHols.region}</span>}
              </Tile>
            )}
          </Group>

          <Group title="Who signs it off">
            <Tile icon={<UserCheck className="w-4 h-4" />} title="Who approves your leave">
              {selfApproves ? (
                <span className="inline-flex items-center gap-1.5 text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Approved automatically &mdash; you sign off your own leave.
                </span>
              ) : approvers.length === 0 ? (
                <span>No approver set &mdash; requests go to an administrator.</span>
              ) : (
                <ol className="space-y-1">
                  {approvers.map((a, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      <span className="text-foreground">{a.name}</span>
                      {a.note && <span className="text-xs text-muted-foreground">&middot; {a.note}</span>}
                    </li>
                  ))}
                </ol>
              )}
            </Tile>

            <Tile icon={<ShieldCheck className="w-4 h-4" />} title="Approval policy">
              {policy ? (
                <span><span className="text-foreground font-medium">{policy.name}</span> &middot; {plural(policy.levels, 'approval step')}</span>
              ) : (
                <span>Standard &mdash; single approver.</span>
              )}
            </Tile>

            {manager && (
              <Tile icon={<User className="w-4 h-4" />} tone="violet" title="Who manages you">
                <span className="text-foreground">{manager.name}</span>
              </Tile>
            )}

            {reach && (
              <Tile icon={<Eye className="w-4 h-4" />} tone="amber" title="Your reach">
                Sees <span className="text-foreground">{reach.view}</span> &middot; manages <span className="text-foreground">{reach.manage}</span>
              </Tile>
            )}

            <Tile icon={<TagIcon className="w-4 h-4" />} title="Your tags">
              {tags.length === 0 ? (
                <span>No tags assigned.</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((t) => (
                    <span key={t.id} className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (t.color || '#64748b') + '22', color: t.color || '#64748b' }}>
                      {t.name}
                    </span>
                  ))}
                </div>
              )}
            </Tile>
          </Group>
        </div>
      )}
    </div>
  );
};

export default YourSetupCard;
